import React, { useContext } from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import type { ChartColorTokens } from '../../utils/colorTokens/chartColorTokens';
import { getChartColorTokenValues } from '../../utils/getColorTokenValue';
import { RendererContext } from '../../../context';

const PlatformLineChart = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_linechart' */
		'@atlassian/visualization/charts'
	).then((module) => ({ default: module.LineChart })),
);

const Title = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_linechart' */
		'@atlassian/visualization/charts'
	).then((module) => ({ default: module.LineChart.elements.Title })),
);

const SubTitle = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_linechart' */
		'@atlassian/visualization/charts'
	).then((module) => ({ default: module.LineChart.elements.SubTitle })),
);

type PlatformLineChartProps = React.ComponentProps<typeof PlatformLineChart>;

/**
 * Excluded props:
 * - grid - does not work across all components
 * - showTooltip - does not work on AVP storybook
 * - showTableOption - does not work across all components and will be moved into a primitive
 * - children - we will not be supporting primitives/elements to compose the chart
 * - sort
 *
 * Limited props:
 * - xAccessor - only allow non function types
 * - yAccessor - only allow non function types
 * - colorAccessor - only allow non function types
 * - colors - only allow chart color tokens (color.chart.*)
 */

export type LineChartProps = Pick<
	PlatformLineChartProps,
	'width' | 'height' | 'data' | 'showBorder'
> & {
	xAccessor: number | string;
	yAccessor: number | string;
	colorAccessor?: number | string;
	title?: string;
	subtitle?: string;
	colors?: ChartColorTokens[];
};

export const LineChart = (props: Parameters<RenderFn>[0]) => {
	const {
		width,
		height,
		data,
		xAccessor,
		yAccessor,
		colorAccessor,
		showBorder,
		colors,
		title,
		subtitle,
	} = props.forgeDoc.props as LineChartProps;

	const colorValues = getChartColorTokenValues(colors);

	// Stop viz components from being rendered in Prod Forge environment
	const { forgeEnvironment } = useContext(RendererContext);
	if (forgeEnvironment === 'PRODUCTION') {
		throw new Error(
			'EAP features are only accessible to users with EAP signup and not supported or recommended for use in production.',
		);
	}

	return (
		<PlatformLineChart
			width={width}
			height={height ? height : 400}
			data={data}
			xAccessor={xAccessor}
			yAccessor={yAccessor}
			colorAccessor={colorAccessor}
			showBorder={showBorder}
			colors={colorValues}
		>
			{title && <Title>{title}</Title>}
			{subtitle && <SubTitle>{subtitle}</SubTitle>}
		</PlatformLineChart>
	);
};
